import { useEffect } from "react";
import { useLocation } from "wouter";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useTranslations } from "@/lib/translations";
import { usePageMeta } from "@/hooks/use-page-meta";
import { CalendarCheck, CheckCircle2 } from "lucide-react";
import { useBookDemo } from "@/components/BookDemoProvider";

const localTimeZone =
  typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : "UTC";

export default function BookedDemo() {
  const { t, language, isRTL } = useTranslations();
  const [, navigate] = useLocation();
  const { bookingResult, reset } = useBookDemo();

  usePageMeta({
    title: "Your demo is booked | ESG Invest",
    description: "Your ESG Invest demo has been scheduled.",
    path: "/bookeddemo",
  });

  useEffect(() => {
    if (!bookingResult) {
      navigate("/", { replace: true });
    }
  }, [bookingResult, navigate]);

  if (!bookingResult) return null;

  const formatLongWhen = (startUtc: string) =>
    new Intl.DateTimeFormat(language === "ar" ? "ar" : "en", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(new Date(startUtc));

  const tzNote = t("booking.timezoneNote").replace("{tz}", localTimeZone);

  const handleDone = () => {
    reset();
    navigate("/");
  };

  return (
    <Layout>
      <section className="py-16 md:py-24" dir={isRTL ? "rtl" : "ltr"}>
        <div className="container mx-auto px-4 max-w-xl">
          <Card
            className="p-8 border-0 shadow-lg text-center space-y-5"
            data-testid="booking-success"
          >
            <div className="flex justify-center">
              <CheckCircle2 className="h-14 w-14 text-accent" />
            </div>
            <h1
              className="text-2xl font-bold text-foreground"
              data-testid="text-booked-title"
            >
              {t("booking.success.title")}
            </h1>

            <div className="rounded-md border border-border bg-muted/40 p-4 text-sm space-y-1">
              <div
                className="flex items-center justify-center gap-2 font-medium text-foreground"
                data-testid="text-booked-when"
              >
                <CalendarCheck className="h-4 w-4 text-accent" />
                {formatLongWhen(bookingResult.startUtc)}
              </div>
              <p className="text-xs text-muted-foreground">{tzNote}</p>
            </div>

            <p
              className="text-sm text-muted-foreground"
              data-testid="text-booked-calendar-note"
            >
              {t("booking.success.desc").replace("{email}", bookingResult.email)}
            </p>

            <Button
              type="button"
              variant="accent"
              size="lg"
              className="w-full"
              onClick={handleDone}
              data-testid="button-booking-done"
            >
              {t("booking.success.close")}
            </Button>
          </Card>
        </div>
      </section>
    </Layout>
  );
}
